var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined") return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});

// src/createBrowserWorker.ts
import { Worker } from "@xylabs/threads/master";
var createBrowserWorker = (url) => {
  try {
    return new Worker(url);
  } catch {
    throw new Error("Unable to create worker");
  }
};

// src/createNodeWorker.ts
import { Worker as Worker2 } from "@xylabs/threads/master";
var createNodeWorker = (func) => {
  try {
    const code = func?.toString().slice(6) ?? "";
    return new Worker2(
      code,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { fromSource: true }
    );
  } catch {
    throw new Error("Unable to create worker");
  }
};

// src/ObjectHasher.ts
import { assertEx } from "@xylabs/assert";
import { asHash, hexFromArrayBuffer } from "@xylabs/hex";
import { ObjectWrapper, omitBy } from "@xylabs/object";
import { subtle } from "@xylabs/platform";
import { Pool, spawn } from "@xylabs/threads/master";
import { WasmSupport } from "@xyo-network/wasm";
import { sha256 } from "hash-wasm";

// src/removeEmptyFields.ts
import { typeOf } from "@xylabs/typeof";
var removeEmptyFields = (obj) => {
  if (obj == null) return obj;
  if (Array.isArray(obj)) {
    return obj.map((value) => typeof value === "object" ? removeEmptyFields(value) : value);
  }
  const newObject = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeOf(value) === "object") {
      newObject[key] = removeEmptyFields(value);
    } else if (value !== void 0) {
      newObject[key] = value;
    }
  }
  return newObject;
};

// src/sortFields.ts
import { isObject } from "@xylabs/object";
var subSort = (value) => {
  return isObject(value) ? sortFields(value) : value;
};
var sortFields = (obj) => {
  const result = {};
  const keys = Object.keys(obj);
  for (const key of keys.toSorted()) {
    result[key] = subSort(obj[key]);
  }
  return result;
};

// src/worker/subtleHashNode.ts
var subtleHashFunc = () => {
  const { subtle: subtle2 } = __require("@xylabs/platform");
  const { expose } = __require("@xylabs/threads/worker");
  expose({
    async hash(data) {
      return await subtle2.digest("SHA-256", data);
    }
  });
};

// src/worker/wasmHashNode.ts
var wasmHashFunc = () => {
  const { sha256: sha2562 } = __require("hash-wasm");
  const { asHash: asHash2 } = __require("@xylabs/hex");
  const { expose } = __require("@xylabs/threads/worker");
  expose({
    async hash(data) {
      return asHash2(await sha2562(data), true);
    }
  });
};

// src/ObjectHasher.ts
var wasmSupportStatic = new WasmSupport(["bigInt"]);
var omitByPredicate = (prefix) => (_, key) => {
  assertEx(typeof key === "string", () => `Invalid key type [${String(key)}, ${typeof key}]`);
  return String(key).startsWith(prefix);
};
var ObjectHasher = class _ObjectHasher extends ObjectWrapper {
  static allowHashPooling = true;
  static allowSubtle = true;
  static createBrowserWorker;
  static createNodeWorker;
  static initialized = (() => {
    globalThis.xyo = globalThis.xyo ?? {};
    if (globalThis.xyo.hashing) {
      console.warn("Two static instances of PayloadHasher detected");
    }
  })();
  static subtleHashWorkerUrl;
  static warnIfUsingJsHash = true;
  static wasmHashWorkerUrl;
  static wasmInitialized = wasmSupportStatic.initialize();
  static wasmSupport = wasmSupportStatic;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static _subtleHashPool;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static _wasmHashPool;
  static get subtleHashPool() {
    if (!this.allowHashPooling || this._subtleHashPool === null) {
      return null;
    }
    try {
      return this._subtleHashPool = this._subtleHashPool ?? (this.subtleHashWorkerUrl ? this.createWorkerPool(this.subtleHashWorkerUrl, subtleHashFunc) : null);
    } catch {
      console.warn("Creating subtle hash worker failed");
      this._subtleHashPool = null;
      return null;
    }
  }
  static get wasmHashPool() {
    if (!this.allowHashPooling || this._wasmHashPool === null) {
      return null;
    }
    try {
      return this._wasmHashPool = this._wasmHashPool ?? (this.wasmHashWorkerUrl ? this.createWorkerPool(this.wasmHashWorkerUrl, wasmHashFunc) : null);
    } catch {
      console.warn("Creating wasm hash worker failed");
      this._wasmHashPool = null;
      return null;
    }
  }
  static createWorker(url, func) {
    if (url) console.debug(`createWorker: ${url}`);
    return assertEx(this.createBrowserWorker?.(url) ?? this.createNodeWorker?.(func), () => "Unable to create worker");
  }
  static async filterExcludeByHash(objs = [], hash) {
    const hashes = Array.isArray(hash) ? hash : [hash];
    return (await this.hashPairs(objs)).filter(([_, objHash]) => !hashes.includes(objHash))?.map((pair) => pair[0]);
  }
  static async filterIncludeByHash(objs = [], hash) {
    const hashes = Array.isArray(hash) ? hash : [hash];
    return (await this.hashPairs(objs)).filter(([_, objHash]) => hashes.includes(objHash))?.map((pair) => pair[0]);
  }
  static async findByHash(objs = [], hash) {
    return (await this.hashPairs(objs)).find(([_, objHash]) => objHash === hash)?.[0];
  }
  /**
   * Asynchronously hashes a payload
   * @param obj A payload
   * @returns The payload hash
   */
  static async hash(obj) {
    const stringToHash = this.stringifyHashFields(obj);
    const enc = new TextEncoder();
    const data = enc.encode(stringToHash);
    if (_ObjectHasher.allowSubtle) {
      try {
        const hashArray = await this.subtleHash(data);
        return hexFromArrayBuffer(hashArray, { bitLength: 256 });
      } catch {
        _ObjectHasher.allowSubtle = false;
      }
    }
    await this.wasmInitialized;
    if (this.wasmSupport.canUseWasm) {
      try {
        return await this.wasmHash(data);
      } catch {
        this.wasmSupport.allowWasm = false;
      }
    }
    throw new Error("No subtle or wasm hashing available");
  }
  static async hashBytes(bytes) {
    const bytesArray = new Uint8Array(bytes);
    if (_ObjectHasher.allowSubtle) {
      const hashArray = await this.subtleHash(bytesArray);
      return hexFromArrayBuffer(hashArray, { bitLength: 256 });
    } else {
      await this.wasmInitialized;
      if (this.wasmSupport.canUseWasm) {
        return await this.wasmHash(bytesArray);
      }
      throw new Error("No subtle or wasm hashing available");
    }
  }
  static hashFields(obj) {
    return sortFields(removeEmptyFields(omitBy(obj, omitByPredicate("_"))));
  }
  /**
   * Creates an array of payload/hash tuples based on the payloads passed in
   * @param objs Any array of payloads
   * @returns An array of payload/hash tuples
   */
  static async hashPairs(objs) {
    return await Promise.all(objs.map(async (obj) => [obj, await _ObjectHasher.hash(obj)]));
  }
  /**
   * Creates an array of payload hashes based on the payloads passed in
   * @param objs Any array of payloads
   * @returns An array of payload hashes
   */
  static async hashes(objs) {
    return objs ? await Promise.all(objs.map((obj) => this.hash(obj))) : void 0;
  }
  /**
   * Returns a clone of the payload that is JSON safe
   * @param obj A payload
   * @param meta Keeps underscore (meta) fields if set to true
   * @returns Returns a clone of the payload that is JSON safe
   */
  static json(payload, meta = false) {
    return sortFields(removeEmptyFields(meta ? payload : omitBy(payload, omitByPredicate("_"))));
  }
  /** @deprecated us json instead */
  static jsonPayload(payload, meta = false) {
    return this.json(payload, meta);
  }
  static stringifyHashFields(obj) {
    return JSON.stringify(this.hashFields(obj));
  }
  static async subtleHash(data) {
    const pool = this.subtleHashPool;
    return pool === null ? await subtle.digest("SHA-256", data) : pool.queue(async (thread) => await thread.hash(data));
  }
  static async wasmHash(data) {
    const pool = this.wasmHashPool;
    return pool === null ? asHash(await sha256(data), true) : pool.queue(async (thread) => await thread.hash(data));
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static createWorkerPool(url, func, size = 8) {
    if (url) console.debug(`createWorkerPool: ${url}`);
    const createFunc = () => spawn(this.createWorker(url, func));
    return Pool(createFunc, size);
  }
  async hash() {
    return await _ObjectHasher.hash(this.obj);
  }
  /**
   * Returns a clone of the payload that is JSON safe
   * @param meta Keeps underscore (meta) fields if set to true
   * @returns Returns a clone of the payload that is JSON safe
   */
  json(meta = false) {
    return _ObjectHasher.json(this.obj, meta);
  }
};

// src/BrowserObjectHasher.ts
ObjectHasher.createBrowserWorker = createBrowserWorker;
ObjectHasher.createNodeWorker = createNodeWorker;
var BrowserObjectHasher = class extends ObjectHasher {
  static createBrowserWorker = createBrowserWorker;
  static createNodeWorker = createNodeWorker;
  static subtleHashWorkerUrl = (() => {
    try {
      return new URL("@xyo-network/hash/worker/subtleHash-bundle.mjs", import.meta.url);
    } catch {
      return;
    }
  })();
  static wasmHashWorkerUrl = (() => {
    try {
      return new URL("@xyo-network/hash/worker/wasmHash-bundle.mjs", import.meta.url);
    } catch {
      return;
    }
  })();
};
var BrowserPayloadHasher = class extends BrowserObjectHasher {
};
export {
  BrowserObjectHasher as ObjectHasher,
  BrowserPayloadHasher as PayloadHasher,
  removeEmptyFields,
  sortFields
};
//# sourceMappingURL=index-browser.mjs.map